import { Outlet, useLocation, useNavigate } from "react-router-dom";
import LayoutNavbar from "../components/navbar/navbar";
import useDarkMode from "use-dark-mode";
import { toast, ToastContainer } from "react-toastify";
import { type ReactNode, useCallback, useEffect, useState } from "react";
import { api } from "@/api";
import { useAppDispatch } from "@/hooks/useAppDispatch.ts";
import {
  setChangeLogModal,
  setPermissions,
  setTour,
  setUser,
} from "../store/user/reducer";
import { Navigation } from "../components/navigation/navigation";
import { Admin } from "@/api/auth/auth.ts";
import { Button, HeroUIProvider } from "@heroui/react";
import { useAppSelector } from "@/hooks/useAppSelector.ts";
import { NotificationIcon } from "../store/notifications/types";
import { addNotification } from "../store/notifications/reducer";
import ym from "react-yandex-metrika";
import { loadHandbooks } from "@/utils/loadHandbooks";
import ChangeLogModal from "../components/modals/change-log/changeLogModal";
import { Tour } from "./tour";

interface BaseDashboardLayoutProps {
  children?: ReactNode;
  withNavbar?: boolean;
  withNavigation?: boolean;
  constrainContent?: boolean;
  className?: string;
  contentClassName?: string;
}

export function BaseDashboardLayout({
  children,
  withNavbar = true,
  withNavigation = true,
  constrainContent = true,
  className = "",
  contentClassName = "",
}: BaseDashboardLayoutProps) {
  const darkMode = useDarkMode(false);
  const businessProfile = useAppSelector(
    (state) => state.businessProfile.businessProfile,
  );
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [user, setDUser] = useState<Admin | null>(null);
  const slocation = useLocation();

  useEffect(() => {
    document
      .querySelector("body")
      ?.classList.remove("dark", "text-foreground", "bg-background");
    document
      .querySelector("body")
      ?.classList.add(
        darkMode.value ? "dark" : "white",
        "text-foreground",
        "bg-background",
      );
  }, [darkMode.value]);

  useEffect(() => {
    api.notifications
      .list()
      .then((res) => {
        res.data.forEach((notification) => {
          dispatch(
            addNotification({
              id: notification.id,
              icon: NotificationIcon.INFO,
              title: notification.title,
              message: notification.message,
              datetime: notification.created_at,
              is_read: notification.is_read,
            }),
          );
        });
      })
      .catch((err) => {
        console.log(err);
      });

    const url = slocation.pathname + slocation.search;
    ym("hit", url);

    api.auth.getCurrentAdminUserInfo().then((data) => {
      setDUser(data.data);
      dispatch(setUser(data.data));
      dispatch(setTour({ isTourActive: data.data.tour_completed }));
    });

    api.auth.getMyPermissions().then((data) => {
      dispatch(
        setPermissions(
          data.data.find((bp) => bp.business_profile_id == businessProfile.id)
            ?.permissions || 0,
        ),
      );
    });

    if (
      location.href.indexOf("selectProfile") == -1 &&
      !localStorage.getItem("businessProfileId")
    ) {
      navigate("/dashboard/selectProfile");
    }

    loadHandbooks({ handbooks: "all" });
  }, [
    slocation.pathname,
    dispatch,
    slocation.search,
    businessProfile.id,
    navigate,
  ]);

  const linkTelegram = useCallback(() => {
    api.auth
      .generateTelegramLink()
      .then((data) => {
        location.href = data.data.link;
      })
      .catch((err) => {
        console.error(err);
        toast.error(
          "У нас произошли некоторые проблемы с привязкой вашего аккаунта к Telegram. Попробуйте позже или обратитесь в поддержку",
        );
      });
  }, []);

  useEffect(() => {
    api.changeLogs.getUnread().then((res) => {
      if (res.data.length > 0) {
        dispatch(setChangeLogModal(true));
      }
    });
  }, [dispatch]);

  const outerClassName = [
    withNavbar ? "layout" : "flex min-h-dvh flex-col",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const contentBaseClass = constrainContent
    ? "layout__content"
    : "flex flex-1 flex-col w-full";

  const mergedContentClass = [contentBaseClass, contentClassName]
    .filter(Boolean)
    .join(" ");

  const renderedChildren = children || <Outlet />;

  return (
    <>
      <ChangeLogModal />
      <HeroUIProvider navigate={navigate}>
        <main
          id="main"
          className={`${darkMode.value ? "dark bg-black" : "bg-zinc-100"} text-foreground`}>
          <ToastContainer
            position="top-right"
            autoClose={5000}
            hideProgressBar={false}
            newestOnTop={true}
            closeOnClick
            style={{ zIndex: 1000000 }}
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
            theme="dark"
          />
          <Tour />
          <div className={outerClassName}>
            {withNavbar ? <LayoutNavbar /> : null}
            <div className={mergedContentClass}>
              {withNavbar &&
                (typeof user?.telegram == "undefined" ||
                  user?.telegram == 0 ||
                  user?.telegram == null) && (
                  <div className="mb-4 flex w-full flex-col gap-2 rounded-xl bg-primary-100 p-4">
                    <span className="text-sm">
                      Заметили, что у вас всё ещё не привязан Telegram. А зря -
                      у нас есть удобный Telegram-бот, облегчающий работу с
                      кабинетом.
                    </span>
                    <Button
                      variant="flat"
                      color="primary"
                      size="sm"
                      onClick={linkTelegram}
                      className="mt-2 max-w-fit">
                      Класс, хочу попробовать
                    </Button>
                  </div>
                )}
              {withNavigation ? <Navigation /> : null}
              {renderedChildren}
            </div>
          </div>
        </main>
      </HeroUIProvider>
    </>
  );
}

export function NavbarDashboardLayout({ children }: { children?: ReactNode }) {
  return (
    <BaseDashboardLayout withNavbar withNavigation constrainContent>
      {children}
    </BaseDashboardLayout>
  );
}

export function Layout({ children }: { children?: ReactNode }) {
  return <NavbarDashboardLayout>{children}</NavbarDashboardLayout>;
}
